import { FC, useEffect, useState } from "react";
import { Modal, Form, Input, Button, Select, Flex, Spin, notification, InputNumber, Tooltip } from "antd";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { PlusOutlined, MinusCircleOutlined, UnorderedListOutlined, } from "@ant-design/icons";
import { ApplicationTypeEnum, ApproveTypeEnum, AssignTypeEnum, Approve, ApproveStepSet, ApproveSet } from "@/types/reimburse";
import { templateGet, templateSet } from "@/apis/reimburse";
import { useTranslation } from "react-i18next";
import { DepartmentSelect, SelectUsers } from "@/components/Selector";
import { User } from "@/types/user";

interface ApproveTemplateDetailProps {
  open: boolean;
  applicationType: ApplicationTypeEnum;
  onClose: () => void;
  onSuccess: (data: Approve) => void;
}

// ApplicationType 选项
const ApplicationTypeOptions = [
  { label: "reimburse.reimbursement", value: ApplicationTypeEnum.REIMBURSEMENT },
  { label: "reimburse.dailySupplies", value: ApplicationTypeEnum.DAILY_SUPPLIES},
  { label: "reimburse.materialOverIssue",value: ApplicationTypeEnum.MATERIAL_OVER_ISSUE},
];

// ApproveType 选项
const ApproveTypeOptions = [
  { label: "reimburse.inOrder", value: ApproveTypeEnum.IN_ORDER },
  { label: "reimburse.any", value: ApproveTypeEnum.ANY },
  { label: "reimburse.last", value: ApproveTypeEnum.LAST },
];

// AssignType 选项
const AssignTypeOptions = [
  { label: "reimburse.specificOrgAnyone", value: AssignTypeEnum.SPECIFIC_ORG_ANYONE },
  { label: "reimburse.applicantOrgAnyone", value: AssignTypeEnum.APPLICANT_ORG_ANYONE },
  { label: "reimburse.specificUsers", value: AssignTypeEnum.SPECIFIC_USERS },
];

const ApproveTemplateDetail: FC<ApproveTemplateDetailProps> = ({
  open,
  applicationType,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [defaultUsers, setDefaultUsers] = useState<User[]>([]);

  useEffect(() => {
    if (open) {
      fetchTemplate();
    }
  }, [open, applicationType]);

  const fetchTemplate = () => {
    setLoading(true);
    templateGet(applicationType)
      .then((res) => {
        setLoading(false);
        // 收集所有 assignUsers 数据作为 defaultUsers
        const allUsers: User[] = [];
        res.steps?.forEach((step) => {
          if (step.assignUsers && step.assignUsers.length > 0) {
            allUsers.push(...step.assignUsers);
          }
        });
        setDefaultUsers(allUsers);

        // 按 stepNo 排序 steps
        const sortedSteps = res.steps?.sort((a, b) => a.stepNo - b.stepNo);

        const initialValues = {
          name: res.name,
          applicationType: res.applicationType,
          approveType: res.approveType,
          description: res.description,
          steps: sortedSteps?.map((step) => ({
            stepNo: step.stepNo,
            assignType: step.assignType,
            assignOrgId: step.assignOrgId || "",
            assignUserIds: step.assignUsers?.map((u) => u.id) || [],
            approveDays: step.approveDays
          })) || [],
        };
        form.setFieldsValue(initialValues);
      })
      .catch(() => {
        // 如果没有模板，设置默认值
        setDefaultUsers([]);
        form.setFieldsValue({
          name: "",
          applicationType,
          approveType: ApproveTypeEnum.IN_ORDER,
          description: "",
          steps: [],
        });
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const params: ApproveSet = {
        approveType: values.approveType,
        name: values.name,
        applicationType: values.applicationType,
        description: values.description || "",
        steps: values.steps.map((step: ApproveStepSet, index: number) => ({
          ...step,
          stepNo: index + 1,
        })),
      };

      let approve = await templateSet(params);
      notification.success({ placement: 'top', message: t("common.updateSuccess") });
      onSuccess(approve);
      handleClose();
    } catch (error) {
      console.error("Form submit error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const steps = form.getFieldValue("steps") || [];
    const reorderedSteps = Array.from(steps);
    const [removed] = reorderedSteps.splice(result.source.index, 1);
    reorderedSteps.splice(result.destination.index, 0, removed);

    form.setFieldValue("steps", reorderedSteps);
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("reimburse.templateConfig")}
      open={open}
      onCancel={handleClose}
      maskClosable={false}
      destroyOnClose
      width={1000}
      centered
      footer={
        <>
          <Button onClick={handleClose}>{t("common.cancel")}</Button>
          <Button type="primary" onClick={handleSubmit} loading={submitting}>
            {t("common.confirm")}
          </Button>
        </>
      }
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item style={{ marginBottom: 8 }} layout="horizontal"
            label={t("common.name")}
            name="name"
            rules={[{ required: true, message: t("reimburse.plName") }]}
          >
            <Input placeholder={t("reimburse.plName")} maxLength={100} />
          </Form.Item>
          <Flex style={{ width: "100%" }} justify="space-between">
            <Form.Item style={{ marginBottom: 8 }} layout="horizontal"
              label={t("reimburse.applicationTypeLabel")}
              name="applicationType"
              rules={[{ required: true, message: t("common.plSelect") }]}
            >
              <Select style={{ minWidth: 220 }}
                disabled options={ApplicationTypeOptions.map((opt) => ({
                  label: t(opt.label),
                  value: opt.value,
                }))} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 8 }} layout="horizontal"
              label={t("reimburse.approveTypeLabel")}
              name="approveType"
              rules={[{ required: true, message: t("common.plSelect") }]}
            >
              <Select style={{ minWidth: 220 }}
                options={ApproveTypeOptions.map((opt) => ({
                  label: t(opt.label),
                  value: opt.value,
                }))}
              />
            </Form.Item>
          </Flex>


          <Form.Item label={t("common.desc")} name="description" style={{ marginBottom: 8 }} layout="horizontal">
            <Input.TextArea
              placeholder={t("common.plDesc")}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item label={t("reimburse.approvalSteps")}>
            <Form.List name="steps">
              {(fields, { add, remove }) => (
                <div style={{ width: "100%", maxHeight: "40vh", overflow: "auto" }} >
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="steps">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {fields.map((field, index) => (
                            <Draggable key={field.key} draggableId={field.key + ''} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 16,
                                    marginBottom: 8,
                                  }}
                                >
                                  <span style={{ fontWeight: "bolder", }}>
                                    {index + 1}.
                                  </span>
                                  <div
                                    {...provided.dragHandleProps}
                                    style={{
                                      flex: 1,
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: "grab",
                                      gap: 8,
                                      padding: 4,
                                      border: "1px solid #d9d9d9",
                                      borderRadius: 4,
                                      background: "#fafafa",
                                    }}
                                  >
                                    <UnorderedListOutlined style={{ fontSize: 20 }} />
                                    <div style={{ flex: 1 }}>
                                      <Form.Item
                                        name={[field.name, "assignType"]}
                                        rules={[{ required: true, message: t("common.plSelect") }]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Select style={{ width: "100%" }}
                                          options={AssignTypeOptions.map((opt) => ({
                                            label: t(opt.label),
                                            value: opt.value,
                                          }))}
                                        />
                                      </Form.Item>
                                    </div>
                                    <Form.Item noStyle shouldUpdate={(prev, curr) => {
                                      const prevType = prev.steps?.[index]?.assignType;
                                      const currType = curr.steps?.[index]?.assignType;
                                      return prevType !== currType;
                                    }}>
                                      {({ getFieldValue }) => {
                                        const assignType = getFieldValue(["steps", index, "assignType"]);

                                        if (assignType === AssignTypeEnum.SPECIFIC_ORG_ANYONE) {
                                          return (
                                            <Form.Item
                                              name={[field.name, "assignOrgId"]}
                                              rules={[{ required: true, message: t("reimburse.plSelectOrg") }]}
                                              style={{ marginBottom: 0, flex: 3, minWidth: 220 }}
                                            >
                                              <DepartmentSelect />
                                            </Form.Item>
                                          );
                                        }

                                        if (assignType === AssignTypeEnum.SPECIFIC_USERS) {
                                          return (
                                            <Form.Item
                                              name={[field.name, "assignUserIds"]}
                                              rules={[{ required: true, message: t("reimburse.plSelectUsers") }]}
                                              style={{ marginBottom: 0, flex: 3 }}
                                            >
                                              <SelectUsers style={{ width: "100%" }}
                                                defaultList={defaultUsers}
                                                getSelUser={user => setDefaultUsers(last => [...last, user])}
                                              />
                                            </Form.Item>
                                          );
                                        }

                                        return null;
                                      }}
                                    </Form.Item>
                                    <div style={{ width: 120 }}>
                                      <Tooltip title={t("reimburse.approvalTimeLimit")}>
                                        <Form.Item
                                          name={[field.name, "approveDays"]}
                                          rules={[{ required: true, message: t("reimburse.timeout") }]}
                                          style={{ marginBottom: 0 }}
                                        >
                                          <InputNumber changeOnWheel min={0} precision={0} style={{ width: "100%" }} addonAfter={t("requirement.day")} placeholder={t("reimburse.timeout")} />
                                        </Form.Item>
                                      </Tooltip>
                                    </div>
                                    {fields.length > 1 && (
                                      <Button
                                        type="text"
                                        danger
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => remove(field.name)}
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  <Button
                    type="dashed"
                    onClick={() => add({ assignType: AssignTypeEnum.APPLICANT_ORG_ANYONE })}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                  >
                    {t("reimburse.addStep")}
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ApproveTemplateDetail;
